import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Trophy, Award, Crown, RefreshCw, Lock, Sparkles, Eye } from "lucide-react";
import {
  REBATE_MEDIA_OWNERS,
  TIER_ORDER,
  TIER_LABEL,
  TIER_ACCENT,
  computeOverallAutoTier,
  computeTierForOwner,
  computeDiscountPercent,
  type RebateTier,
} from "@/config/affiliateRebates";
import { RebatesRewardsDrawer } from "./RebatesRewardsDrawer";

interface Affiliate {
  user_id: string;
  applicant_name: string;
  email: string;
  company_name: string | null;
}

interface PeriodRow {
  id: string;
  affiliate_user_id: string;
  year: number;
  quarter: number;
  total_sales: number;
  auto_tier: RebateTier | null;
  assigned_tier: RebateTier | null;
  manual_override: boolean;
  override_reason: string | null;
  finalized_at: string | null;
}

const TIER_ICON: Record<RebateTier, typeof Award> = {
  silver: Award,
  gold: Trophy,
  platinum: Crown,
};

const currentQuarter = () => {
  const m = new Date().getMonth();
  return Math.floor(m / 3) + 1;
};

export const AffiliateRebatesPanel = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { language } = useLanguage();
  const isAr = language === "ar";

  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [quarter, setQuarter] = useState<number>(currentQuarter());
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [periods, setPeriods] = useState<Record<string, PeriodRow>>({});
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [drawerAffiliate, setDrawerAffiliate] = useState<{ affiliate: Affiliate; period: PeriodRow | null } | null>(null);

  // ── Load approved affiliates ──
  const loadAffiliates = useCallback(async () => {
    const { data, error } = await supabase
      .from("affiliate_applications")
      .select("user_id, applicant_name, email, company_name")
      .eq("approval_status", "approved")
      .order("applicant_name");
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    setAffiliates((data ?? []) as Affiliate[]);
  }, [toast]);

  // ── Load existing periods for the selected quarter ──
  const loadPeriods = useCallback(async () => {
    const { data, error } = await supabase
      .from("affiliate_rebate_periods")
      .select("*")
      .eq("year", year)
      .eq("quarter", quarter);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    const map: Record<string, PeriodRow> = {};
    (data ?? []).forEach((p) => {
      map[p.affiliate_user_id] = p as PeriodRow;
    });
    setPeriods(map);
  }, [year, quarter, toast]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadAffiliates(), loadPeriods()]).finally(() => setLoading(false));
  }, [loadAffiliates, loadPeriods]);

  // ── Recompute sales for every affiliate via the RPC ──
  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      for (const a of affiliates) {
        const { data: salesData, error: rpcError } = await supabase.rpc(
          "get_affiliate_quarter_sales" as never,
          { p_affiliate_user_id: a.user_id, p_year: year, p_quarter: quarter } as never
        );
        if (rpcError) {
          console.error("Sales RPC failed for", a.user_id, rpcError);
          continue;
        }
        const total = Number(salesData ?? 0);
        const autoTier = computeOverallAutoTier(total);
        const existing = periods[a.user_id];
        const newAssigned = existing?.manual_override
          ? existing.assigned_tier
          : autoTier;

        const payload = {
          affiliate_user_id: a.user_id,
          year,
          quarter,
          total_sales: total,
          auto_tier: autoTier,
          assigned_tier: newAssigned,
        };

        if (existing) {
          await supabase
            .from("affiliate_rebate_periods")
            .update(payload)
            .eq("id", existing.id);
        } else {
          await supabase.from("affiliate_rebate_periods").insert(payload);
        }
      }
      await loadPeriods();
      toast({
        title: isAr ? "تم تحديث المبيعات" : "Sales recomputed",
        description: isAr
          ? "تم حساب مبيعات الربع لجميع الشركاء."
          : "Quarterly sales totals refreshed for all affiliates.",
      });
    } catch (e) {
      console.error(e);
      toast({ title: "Error", description: String(e), variant: "destructive" });
    } finally {
      setRecomputing(false);
    }
  };

  // ── Update assigned tier for one affiliate (admin override) ──
  const updateAssignedTier = async (
    affiliateId: string,
    newTier: RebateTier | null
  ) => {
    const existing = periods[affiliateId];
    if (!existing) {
      toast({
        title: isAr ? "احسب المبيعات أولاً" : "Recompute sales first",
        description: isAr
          ? "اضغط 'تحديث المبيعات' قبل تعيين الرتب."
          : "Click 'Recompute Sales' before assigning tiers.",
        variant: "destructive",
      });
      return;
    }
    const isManual = newTier !== existing.auto_tier;
    const { error } = await supabase
      .from("affiliate_rebate_periods")
      .update({
        assigned_tier: newTier,
        manual_override: isManual,
      })
      .eq("id", existing.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    await loadPeriods();
  };

  const toggleOverride = async (affiliateId: string, on: boolean) => {
    const existing = periods[affiliateId];
    if (!existing) return;
    const { error } = await supabase
      .from("affiliate_rebate_periods")
      .update({
        manual_override: on,
        assigned_tier: on ? existing.assigned_tier : existing.auto_tier,
      })
      .eq("id", existing.id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    await loadPeriods();
  };

  const updateOverrideReason = async (affiliateId: string, reason: string) => {
    const existing = periods[affiliateId];
    if (!existing) return;
    await supabase
      .from("affiliate_rebate_periods")
      .update({ override_reason: reason })
      .eq("id", existing.id);
  };

  // ── Finalize quarter: lock tiers and grant rewards ──
  const handleFinalize = async () => {
    if (
      !confirm(
        isAr
          ? "هل أنت متأكد من إنهاء هذا الربع ومنح المكافآت؟ لا يمكن التراجع بسهولة."
          : "Finalize this quarter and grant all reward credits? This cannot be easily undone."
      )
    )
      return;

    setFinalizing(true);
    try {
      const finalizedAt = new Date().toISOString();
      for (const aff of affiliates) {
        const period = periods[aff.user_id];
        if (!period || !period.assigned_tier) continue;

        // Lock period
        await supabase
          .from("affiliate_rebate_periods")
          .update({
            finalized_at: finalizedAt,
            finalized_by: user?.id ?? null,
          })
          .eq("id", period.id);

        // Grant a reward row per media owner that the affiliate qualifies for
        for (const owner of REBATE_MEDIA_OWNERS) {
          const ownerTier = computeTierForOwner(period.total_sales, owner);
          if (!ownerTier) continue;

          const screens = owner.screenRewards[ownerTier].screens;
          await supabase.from("affiliate_rebate_rewards").insert({
            period_id: period.id,
            affiliate_user_id: aff.user_id,
            media_owner_name: owner.name,
            reward_type: "screens",
            tier: ownerTier,
            quantity: screens,
            duration_weeks: owner.screenRewards[ownerTier].durationWeeks,
          });

          if (owner.discount) {
            const pct = computeDiscountPercent(period.total_sales, ownerTier, owner);
            if (pct > 0) {
              await supabase.from("affiliate_rebate_rewards").insert({
                period_id: period.id,
                affiliate_user_id: aff.user_id,
                media_owner_name: owner.name,
                reward_type: "discount",
                tier: ownerTier,
                quantity: pct,
                duration_weeks: 0,
              });
            }
          }
        }
      }
      await loadPeriods();
      toast({
        title: isAr ? "تم إنهاء الربع" : "Quarter finalized",
        description: isAr ? "تم منح المكافآت بنجاح." : "Reward credits have been granted.",
      });
    } catch (e) {
      console.error(e);
      toast({ title: "Error", description: String(e), variant: "destructive" });
    } finally {
      setFinalizing(false);
    }
  };

  const yearOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    return [cy - 2, cy - 1, cy, cy + 1];
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                {isAr ? "نظام المكافآت بالقيمة" : "Value Rebate System"}
              </CardTitle>
              <CardDescription>
                {isAr
                  ? "إدارة الرتب الفصلية ومنح مكافآت الشاشات للشركاء."
                  : "Manage quarterly tiers and grant screen-credit rewards to affiliates."}
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={String(quarter)} onValueChange={(v) => setQuarter(Number(v))}>
                <SelectTrigger className="w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4].map((q) => (
                    <SelectItem key={q} value={String(q)}>
                      Q{q}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleRecompute} disabled={recomputing} variant="outline">
                <RefreshCw className={`h-4 w-4 mr-2 ${recomputing ? "animate-spin" : ""}`} />
                {isAr ? "تحديث المبيعات" : "Recompute Sales"}
              </Button>
              <Button onClick={handleFinalize} disabled={finalizing}>
                <Lock className="h-4 w-4 mr-2" />
                {isAr ? "إنهاء الربع" : "Finalize Quarter"}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Affiliates table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {isAr ? "الشركاء المعتمدون" : "Approved Affiliates"} ({affiliates.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">
              {isAr ? "جاري التحميل..." : "Loading..."}
            </div>
          ) : affiliates.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              {isAr ? "لا يوجد شركاء معتمدون" : "No approved affiliates"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{isAr ? "الشريك" : "Affiliate"}</TableHead>
                    <TableHead className="text-right">{isAr ? "مبيعات الربع" : "Quarter Sales"}</TableHead>
                    <TableHead>{isAr ? "الرتبة التلقائية" : "Auto Tier"}</TableHead>
                    <TableHead>{isAr ? "الرتبة المعينة" : "Assigned Tier"}</TableHead>
                    <TableHead>{isAr ? "تجاوز يدوي" : "Override"}</TableHead>
                    <TableHead>{isAr ? "السبب" : "Reason"}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {affiliates.map((a) => {
                    const p = periods[a.user_id];
                    const sales = p?.total_sales ?? 0;
                    const autoTier = p?.auto_tier ?? null;
                    const assigned = p?.assigned_tier ?? null;
                    const finalized = !!p?.finalized_at;

                    return (
                      <TableRow key={a.user_id}>
                        <TableCell>
                          <div className="font-medium text-foreground">{a.applicant_name}</div>
                          <div className="text-xs text-muted-foreground">{a.email}</div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          {sales.toLocaleString()} SAR
                        </TableCell>
                        <TableCell>
                          {autoTier ? <TierBadge tier={autoTier} isAr={isAr} /> : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={assigned ?? "none"}
                            onValueChange={(v) =>
                              updateAssignedTier(a.user_id, v === "none" ? null : (v as RebateTier))
                            }
                            disabled={finalized}
                          >
                            <SelectTrigger className="w-[130px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">{isAr ? "بدون" : "None"}</SelectItem>
                              {TIER_ORDER.map((t) => (
                                <SelectItem key={t} value={t}>
                                  {TIER_LABEL[t][isAr ? "ar" : "en"]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={p?.manual_override ?? false}
                            onCheckedChange={(c) => toggleOverride(a.user_id, c)}
                            disabled={!p || finalized}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            defaultValue={p?.override_reason ?? ""}
                            placeholder={isAr ? "اختياري" : "Optional"}
                            className="h-9 w-[180px]"
                            disabled={!p?.manual_override || finalized}
                            onBlur={(e) => updateOverrideReason(a.user_id, e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDrawerAffiliate({ affiliate: a, period: p ?? null })}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            {isAr ? "المكافآت" : "Rewards"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tier configuration reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {isAr ? "مرجع نظام الرتب" : "Tier Configuration Reference"}
          </CardTitle>
          <CardDescription>
            {isAr
              ? "Waki وشركاء 'قريباً' غير مشمولين. خصومات Attention و On Spot لم تُحدد بعد."
              : "Waki + 'coming soon' partners excluded. Discount systems for Attention & On Spot are TBD."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {TIER_ORDER.map((tier) => {
              const accent = TIER_ACCENT[tier];
              const Icon = TIER_ICON[tier];
              return (
                <div
                  key={tier}
                  className={`rounded-xl border border-border/60 bg-gradient-to-br ${accent.from} ${accent.to} p-4`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className={`h-5 w-5 ${accent.text}`} />
                    <span className={`text-sm font-bold uppercase tracking-wider ${accent.text}`}>
                      {TIER_LABEL[tier][isAr ? "ar" : "en"]}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {REBATE_MEDIA_OWNERS.map((o) => (
                      <div key={o.name} className="flex justify-between text-xs bg-card/70 rounded-md px-2 py-1.5">
                        <span className="font-medium text-foreground">{o.name}</span>
                        <span className="text-muted-foreground tabular-nums">
                          ≥{(o.thresholds[tier] / 1000).toFixed(0)}k → {o.screenRewards[tier].screens} {isAr ? "شاشة" : "screens"}
                          {o.discount ? ` (+${o.discount.capByTier[tier]}%)` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {drawerAffiliate && (
        <RebatesRewardsDrawer
          open={!!drawerAffiliate}
          onClose={() => setDrawerAffiliate(null)}
          affiliateUserId={drawerAffiliate.affiliate.user_id}
          affiliateName={drawerAffiliate.affiliate.applicant_name}
          period={drawerAffiliate.period}
          onChanged={loadPeriods}
        />
      )}
    </div>
  );
};

const TierBadge = ({ tier, isAr }: { tier: RebateTier; isAr: boolean }) => {
  const accent = TIER_ACCENT[tier];
  const Icon = TIER_ICON[tier];
  return (
    <Badge variant="outline" className={`gap-1 ${accent.text} border-current/30`}>
      <Icon className="h-3 w-3" />
      {TIER_LABEL[tier][isAr ? "ar" : "en"]}
    </Badge>
  );
};

export default AffiliateRebatesPanel;
